import { useRef, useState } from "react";
import { UploadCloud, Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, errorMessage, thumbUrl } from "@/lib/api";

export const UploadBox = ({ upload, onUploaded, width, height }) => {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);

  const handle = async (file) => {
    if (!file) return;
    if (file.type !== "image/png" && !file.name.toLowerCase().endsWith(".png")) {
      toast.error(t("errors.not_png"));
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd);
      setLocalPreview(URL.createObjectURL(file));
      onUploaded(data);
    } catch (e) {
      toast.error(errorMessage(e, t));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const maxW = upload ? upload.width_px / 300 : 0;
  const maxH = upload ? upload.height_px / 300 : 0;
  const soft = upload && width > 0 && height > 0 && (width > maxW + 0.01 || height > maxH + 0.01);

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/png,.png" className="hidden" data-testid="file-upload-input" onChange={(e) => handle(e.target.files?.[0])} />
      {!upload ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
          data-testid="upload-dropzone"
          className={`flex w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors ${drag ? "border-gold bg-gold/5" : "border-white/20 hover:border-gold/70"}`}
        >
          {busy ? <Loader2 className="h-9 w-9 animate-spin text-gold" /> : <UploadCloud className="h-9 w-9 text-gold" />}
          <span className="font-bold">{busy ? t("builder.uploading") : t("builder.drop")}</span>
          <span className="text-xs text-slate-400">{t("builder.drop_sub")}</span>
        </button>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[200px_1fr]" data-testid="upload-preview">
          <div className="checker grid aspect-square place-items-center overflow-hidden rounded-md p-3">
            <img src={localPreview || thumbUrl(upload.id)} alt={upload.original_filename} className="max-h-full max-w-full object-contain" data-testid="upload-preview-img" />
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <p className="flex items-center gap-2 break-all font-bold"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> {upload.original_filename}</p>
            <p className="font-mono-num text-slate-300" data-testid="upload-px">
              {t("builder.px", { w: upload.width_px, h: upload.height_px })} · {upload.dpi ? t("builder.dpi", { dpi: upload.dpi }) : t("builder.dpi_unknown")}
            </p>
            <p className="text-slate-400">{t("builder.sharp", { w: maxW.toFixed(1), h: maxH.toFixed(1) })}</p>
            {soft && (
              <p className="flex items-start gap-2 rounded-md bg-amber-500/10 p-2 text-amber-300" data-testid="upload-soft-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("builder.warn_soft")}
              </p>
            )}
            <button type="button" onClick={() => inputRef.current?.click()} className="mt-auto inline-flex w-fit items-center gap-2 font-bold text-gold hover:underline" data-testid="upload-replace-btn" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("builder.replace")}
            </button>
          </div>
        </div>
      )}
      <ul className="mt-4 flex flex-wrap gap-2">
        {t("builder.rules").map((r) => (
          <li key={r} className="rounded-full border border-white/10 bg-ink/60 px-3 py-1 text-xs font-bold text-slate-300">{r}</li>
        ))}
      </ul>
    </div>
  );
};
